import React, { useMemo, useState } from "react";
import ActivityLoader from "../Common/ActivityLoader";
import CallOut from "../Common/CallOut";
import { useAccounts } from "../../DataStore/AccountsContext";

const AccountUpdateModal = ({
  show,
  line,
  saving = false,
  onClose,
  onSave,
}) => {
  const {
    accountsCatalogue,
    getAllAccounts,
    getPrimitiveAccountsByType,
  } = useAccounts();

  const [selectedAccountId, setSelectedAccountId] = useState("");

  React.useEffect(() => {
    if (!show || !line) return;

    setSelectedAccountId(line.account_id || "");
    getAllAccounts();
  }, [show, line]);

  const accounts = useMemo(() => {
    if (!line?.account_type) return [];
    return getPrimitiveAccountsByType(line.account_type);
  }, [line, accountsCatalogue]);

  const selectedAccount = useMemo(() => {
    return (
      accounts.find(
        (a) => String(a.account_id) === String(selectedAccountId)
      ) || null
    );
  }, [accounts, selectedAccountId]);

  if (!show || !line) return null;

  const isSameAccount = String(line.account_id) === String(selectedAccountId);
  const loadingAccounts = !Array.isArray(accountsCatalogue);

  const handleSave = () => {
    if (!selectedAccount || isSameAccount) return;

    onSave?.({
      line,
      account: selectedAccount,
    });
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">Change Transacting Account</h5>
              <small className="text-muted">
                Showing {line.account_type || "same type"} accounts only.
              </small>
            </div>

            <button
              type="button"
              className="close"
              onClick={onClose}
              disabled={saving}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="alert alert-light border">
              <div className="text-muted small mb-1">Current Account</div>
              <div className="font-weight-bold">
                {line.account_number} — {line.account_name}
              </div>
              <div className="small text-muted mt-1">
                Type: {line.account_type || "—"}
              </div>
            </div>

            <div className="form-group">
              <label className="font-weight-bold">Select New Account</label>

              {loadingAccounts ? (
                <div className="py-2">
                  <ActivityLoader width={18} />
                </div>
              ) : (
                <select
                  className="form-control"
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select account</option>
                  {accounts.map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.account_number} — {account.account_name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {!loadingAccounts && accounts.length === 0 && (
              <CallOut
                type="info"
                title="No Accounts Found"
                message={`No primitive ${line.account_type || ""} accounts were found.`}
              />
            )}

            {selectedAccount && !isSameAccount && (
              <div
                className="mt-3 p-3 rounded border"
                style={{ background: "#f8fafc" }}
              >
                <div className="text-muted small mb-2">
                  Account Change Preview
                </div>

                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <span className="badge badge-light border">
                    {line.account_number} — {line.account_name}
                  </span>

                  <i className="fa fa-arrow-right text-muted" />

                  <span className="badge badge-primary">
                    {selectedAccount.account_number} —{" "}
                    {selectedAccount.account_name}
                  </span>
                </div>
              </div>
            )}

            {isSameAccount && (
              <div className="text-muted small mt-2">
                Select a different account to enable update.
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !selectedAccountId || isSameAccount}
            >
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="fa fa-save mr-2" />
                  Update Account
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountUpdateModal;